import { useEffect, useMemo, useState } from "react";
import useSearchStore from "@/stores/useSearchStore";
import { CompanySearchResultDto } from "@advanced-sales-technologies/backend-client/models/CompanySearchResultDto";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useGetOrgaInfo } from "@/hooks/api/organisation/useGetOrgaInfo";
import { SearchParams, searchQueryKey } from "@/hooks/api/search/useInfiniteSearch";
import { useTableConfig } from "@/hooks/table/useTableConfig";
import useTierLimit from "@/hooks/useTierLimit";
import CompanyDetailPanel from "@/components/molecules/company/detail/CompanyDetailPanel";
import SearchLimitReached from "@/components/molecules/search/SearchLimitReached";
import ConfigureTableColumnsButton from "@/components/molecules/table/ConfigureTableColumnsButton";
import NoSearchResults from "@/components/organisms/search/NoSearchResults";
import { CompanyListColumns, searchColumns, searchColumnsDef } from "@/components/organisms/search/searchColumns";
import StaticModalHandler from "@/components/organisms/StaticModalHandler";
import Table from "@/components/organisms/table/Table";

type Props = {
  searchParams: SearchParams;
  searchQueryHook: UseInfiniteQueryResult<InfiniteData<CompanySearchResultDto, unknown>, Error>;
};

const SearchResults = ({ searchParams, searchQueryHook }: Props) => {
  const { t } = useTranslation();
  const [showPersonColumns, setShowPersonColumns] = useState<boolean>(false);
  const { data: organisationData } = useGetOrgaInfo();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, isFetched } = searchQueryHook;

  const { openDetail, closeDetail, detailId } = useSearchStore((state) => state);

  const hasLists = useMemo(() => (organisationData?.listCount || 0) > 0, [organisationData?.listCount]);
  const { tierLimit } = useTierLimit();

  const flattenedData = useMemo(() => data?.pages?.flatMap((page) => page.data) ?? [], [data?.pages]);
  const detailCompany = useMemo(() => {
    return flattenedData.find((company) => company.id === detailId);
  }, [flattenedData, detailId]);

  const reachedLimit = useMemo(
    () => flattenedData.length >= tierLimit && (data?.pages[0].info?.items_total || 0) > tierLimit,
    [flattenedData.length, data?.pages, tierLimit],
  );

  const columnNames = useMemo(() => searchColumns(t), [t]);
  const columnOrder = useMemo(() => {
    // always-shown columns
    const baseColumns: CompanyListColumns[] = [
      CompanyListColumns.COMPANY_NAME,
      CompanyListColumns.STATUS,
      CompanyListColumns.INDUSTRIES,
      CompanyListColumns.WEBSITE,
      CompanyListColumns.POINT_OF_CONTACT_POSITION,
      CompanyListColumns.POINT_OF_CONTACT_FULL_NAME,
      CompanyListColumns.POINT_OF_CONTACT_EMAIL,
    ];

    // person columns only when showPersonColumns === true
    const personColumns: CompanyListColumns[] = showPersonColumns
      ? [CompanyListColumns.POINT_OF_CONTACT_PHONE, CompanyListColumns.POINT_OF_CONTACT_LINKEDIN]
      : [];

    // the rest of the columns
    const otherColumns: CompanyListColumns[] = [
      CompanyListColumns.CONTACT_INFO_EMAIL,
      CompanyListColumns.CONTACT_INFO_PHONE,
      CompanyListColumns.OFFICE_ADDRESS,
      CompanyListColumns.OFFICE_STREET,
      CompanyListColumns.DESCRIPTION,
      CompanyListColumns.HEADCOUNT_RANGE,
      CompanyListColumns.REVENUE_RANGE,
      CompanyListColumns.EARNINGS_RANGE,
      CompanyListColumns.LINKEDIN_URL,
    ];

    return [...baseColumns, ...personColumns, ...otherColumns].filter(
      (column) => !(column === CompanyListColumns.STATUS && !searchParams.showTerminated),
    );
  }, [searchParams.showTerminated, showPersonColumns]);

  const columns = useMemo(
    () =>
      searchColumnsDef({
        t,
        searchParams,
        queryKey: [searchQueryKey, searchParams],
        onShowPersonColumns: setShowPersonColumns,
      }),
    [t, searchParams],
  );
  const columnValues = useMemo(() => Object.values(columnNames), [columnNames]);

  const tableConfig = useTableConfig({
    columns: columnValues,
    initialColumnOrder: columnOrder,
  });

  const selectedCompanies = useMemo(() => {
    const selectedIds = Object.entries(tableConfig.rowSelection)
      .filter(([, isChecked]) => isChecked)
      .map(([id]) => id);

    if (selectedIds.length === 0) return [];

    const companyMap = new Map(flattenedData.map((c) => [c.id, c]));
    return selectedIds.map((id) => companyMap.get(id) ?? id);
  }, [flattenedData, tableConfig.rowSelection]);

  useEffect(() => {
    tableConfig.clearRowSelection();
  }, [searchParams]);

  if (isFetched && flattenedData.length === 0) {
    return <NoSearchResults />;
  }

  const totalResults = new Intl.NumberFormat().format(data?.pages[0].info?.items_total || 0);

  return (
    <div className={"flex h-screen flex-grow flex-col overflow-hidden pb-4 pl-6 pr-2 pt-5"}>
      <div className={"mb-2 flex min-h-12 items-start justify-between pr-4"}>
        <h2 className={"flex items-center gap-2 text-2xl font-semibold"}>
          {isLoading ? (
            <div className="h-7 w-20 animate-pulse rounded-full bg-gray-300" />
          ) : (
            <span>{totalResults}</span>
          )}
          {t("search.results")}
        </h2>
        {Object.values(tableConfig.rowSelection).filter((selected) => selected).length > 0 && (
          <StaticModalHandler
            selectedCompanies={selectedCompanies}
            hasLists={hasLists}
            resetTableSelection={() => tableConfig.clearRowSelection()}
          />
        )}
      </div>

      <div className={"mb-6 flex justify-between gap-3 pr-2"}>
        <div />
        <ConfigureTableColumnsButton columnNames={columnNames} columns={columns} {...tableConfig} />
      </div>

      <Table
        allColumns={columns}
        data={flattenedData}
        isLoading={isLoading}
        loadMore={fetchNextPage}
        hasMoreData={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        showHeaderWhileLoading
        canReorderColumns={true}
        canReorderRows={false}
        {...tableConfig}
        tableOptions={{
          columnResizeDirection: "ltr",
          columnResizeMode: "onChange",
          getRowId: (row) => row.id,
          enableRowSelection: true,
        }}
        isEndOfData={reachedLimit && !isLoading}
        endOfDataComponent={<SearchLimitReached isEnterprise={organisationData?.is_enterprise || false} />}
        onDetailClicked={openDetail}
        activeDetailId={detailId}
      />
      <CompanyDetailPanel id={detailId} isOpen={Boolean(detailId)} onClose={closeDetail} company={detailCompany} />
    </div>
  );
};

export default SearchResults;
